/**
 * Ridalo date/time formatting helpers.
 * All timestamps are stored in UTC in the DB. On the client, we display them in
 * the connected user's LOCAL timezone (Intl API), so a traveler in France sees
 * French time, a provider in Thailand sees Bangkok time, etc.
 * Never expose raw ISO strings to end users.
 */

// Detect the browser's IANA timezone (falls back to Asia/Bangkok for SSR).
const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Bangkok';
  } catch {
    return 'Asia/Bangkok';
  }
})();

/** Human-friendly label used in tooltips and "local time" notes. */
export const USER_TIMEZONE = TZ;
export const USER_TIMEZONE_LABEL = (() => {
  try {
    const parts = new Intl.DateTimeFormat('fr-FR', { timeZoneName: 'short', timeZone: TZ }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || TZ;
  } catch {
    return TZ;
  }
})();

/**
 * Backend MongoDB serializes datetimes as ISO strings without a timezone marker
 * (e.g. "2026-07-24T14:24:59.847000"). Per ES2020+, such strings are parsed as
 * LOCAL time by browsers — but they are actually UTC on the server.
 * Normalize by appending "Z" when the string has time but no explicit offset,
 * so `new Date(...)` returns the true UTC instant and Intl can shift it to the
 * viewer's local zone correctly (and stably across refreshes).
 */
const normalizeIso = (v) => {
  if (typeof v !== 'string') return v;
  // Already has offset (Z, +hh:mm, -hh:mm) → leave untouched.
  if (/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(v)) return v;
  // Has date + time (with optional fractional seconds) → assume UTC.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(v)) return `${v}Z`;
  return v;
};

const parts = (iso) => {
  if (!iso) return null;
  try {
    // Accept both ISO strings and Date objects.
    const d = iso instanceof Date ? iso : new Date(normalizeIso(iso));
    if (Number.isNaN(d.getTime())) return null;
    const dt = new Intl.DateTimeFormat('fr-FR', {
      timeZone: TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const get = (t) => dt.find((p) => p.type === t)?.value;
    return {
      day: get('day'), month: get('month'), year: get('year'),
      hour: get('hour'), minute: get('minute'),
    };
  } catch {
    return null;
  }
};

/** "13/11/2026" — Bangkok time */
export const formatDate = (iso) => {
  const p = parts(iso);
  return p ? `${p.day}/${p.month}/${p.year}` : '—';
};

/** "09:00" — Bangkok time */
export const formatTime = (iso) => {
  const p = parts(iso);
  return p ? `${p.hour}:${p.minute}` : '—';
};

/** "13/11/2026 à 09:00" — Bangkok time */
export const formatDT = (iso) => {
  const p = parts(iso);
  return p ? `${p.day}/${p.month}/${p.year} à ${p.hour}:${p.minute}` : '—';
};

/**
 * Format a date-only string ("YYYY-MM-DD") without a time component.
 * Uses direct string manipulation so we don't add spurious hour info.
 */
export const formatDateOnly = (ymd) => {
  if (!ymd) return '—';
  const s = String(ymd).split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return formatDate(ymd);
};

/**
 * Combine a YYYY-MM-DD date-only + optional HH:mm time into "DD/MM/YYYY à HH:mm".
 * If time is empty, returns just "DD/MM/YYYY".
 */
export const formatDateTime = (ymd, hm) => {
  const d = formatDateOnly(ymd);
  if (!hm) return d;
  return `${d} à ${hm}`;
};

/**
 * Format an ISO string that represents Bangkok WALL CLOCK time (naive, no
 * offset — as produced by the settlement backend) into
 * "DD/MM/YYYY HH:mm Asia/Bangkok (UTC+7)".
 *
 * We deliberately parse the pattern with a regex so the browser's local
 * timezone can NEVER shift the values (the backend already computed the
 * boundaries in Bangkok time).
 */
export const formatBangkokDT = (iso) => {
  if (!iso) return '—';
  const s = String(iso);
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return '—';
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
};

/**
 * Format a settlement period as
 * "14/09/2026 00:00 → 20/09/2026 23:59 Asia/Bangkok (UTC+7)".
 */
export const formatBangkokPeriod = (startIso, endIso) => {
  const start = formatBangkokDT(startIso);
  const end = formatBangkokDT(endIso);
  if (start === '—' || end === '—') return '—';
  return `${start} → ${end} Asia/Bangkok (UTC+7)`;
};

/** Just the Bangkok label for reuse. */
export const BANGKOK_TZ_LABEL = 'Asia/Bangkok (UTC+7)';

/**
 * Format any UTC ISO timestamp into Bangkok wall-clock time — regardless of
 * the viewer's browser timezone. Used for business notifications (payments,
 * settlements) that always describe an event happening in Thailand.
 * Output: "13/11/2026 à 09:00 Asia/Bangkok (UTC+7)"
 */
export const formatBangkokLive = (iso) => {
  if (!iso) return '—';
  try {
    const d = iso instanceof Date ? iso : new Date(normalizeIso(iso));
    if (Number.isNaN(d.getTime())) return '—';
    const dt = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const get = (t) => dt.find((p) => p.type === t)?.value;
    return `${get('day')}/${get('month')}/${get('year')} à ${get('hour')}:${get('minute')} ${BANGKOK_TZ_LABEL}`;
  } catch {
    return '—';
  }
};
